<template>
  <div class="md-layout text-center">
    <div class="md-layout-item md-size-100">
      <p class="sub_title">Signing you in&hellip;</p>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { cioanalytics, posthog } from "@/main";

// Same localStorage key every other auth call in the app uses for the client session_id.
const SESSION_KEY = "vue-authenticate.vueauth_access_token";
const TICKET_PATTERN = /^[0-9a-f]{64}$/;
const SSO_CODE_PATTERN = /^sso_[a-z_]{1,40}$/;

function toLogin(next, code) {
  next({ name: "Login", query: { sso_error: code } });
}

/**
 * Landing page for the API's SSO callback: /sso/complete?ticket=...
 *
 * The ticket is redeemed in beforeRouteEnter, i.e. BEFORE the navigation is confirmed. That
 * matters: router.afterEach (router/index.js) and other layouts call /session/authenticate on
 * every navigation, and authenticateUser() pushes to Login when the session has no user. Binding
 * the session first means that first /session/authenticate already sees the logged-in user.
 *
 * The API binds the session only if the session_id we send (our own stored one) is the one that
 * started the flow, so a ticket from someone else's flow is useless here.
 */
export default {
  name: "sso-complete",

  async beforeRouteEnter(to, from, next) {
    const ticket = to.query ? to.query.ticket : null;
    const sessionId = localStorage.getItem(SESSION_KEY);
    if (typeof ticket !== "string" || !TICKET_PATTERN.test(ticket) || !sessionId) {
      return toLogin(next, "sso_ticket_invalid");
    }

    let data = null;
    try {
      const res = await axios.post(
        `${process.env.VUE_APP_API_BASE_URL}/session/sso/complete`,
        { ticket, session_id: sessionId },
        { timeout: 20000 }
      );
      data = res && res.data ? res.data : null;
    } catch (e) {
      return toLogin(next, "sso_ticket_invalid");
    }

    if (data && data.error === false && data.logged_in === true) {
      return next((vm) => vm.finishLogin());
    }

    const code =
      data && typeof data.errorCode === "string" && SSO_CODE_PATTERN.test(data.errorCode)
        ? data.errorCode
        : "sso_ticket_invalid";
    return toLogin(next, code);
  },

  methods: {
    // The password-login success path (store/modules/auth.js login() + Login.vue login()):
    // mark authenticated, load the user through /session/authenticate, reset the prospect
    // list cart state, analytics, then go to Search.
    async finishLogin() {
      this.$store.commit("isAuthenticated", { isAuthenticated: true });
      try {
        await this.$store.dispatch("authenticateUser");
      } catch (e) {
        // authenticateUser records its own errors in the store.
      }
      try {
        this.$store.commit("PS_SET_ID", null);
        this.$store.commit("PS_SET_PRICE", null);
        this.$store.commit("PS_SET_TOTAL", null);
      } catch (e) {
        await this.$store.dispatch("alerts/error", "Failed fully initializing app!");
      }
      try {
        cioanalytics.track("login", { success: true, method: "sso" });
        posthog.capture("user_logged_in", { method: "sso" });
      } catch (e) {
        // analytics must never block sign-in
      }
      // replace: the spent ticket URL should not stay in history.
      this.$router.replace({ name: "Search" });
    },
  },
};
</script>
